import { ComandoPipeline, EndpointBasico, problemasDeTokens } from '../autenticacao-config/autenticacao.util';

export type NivelPipeline = 'erro' | 'aviso';

/** Problema encontrado no fluxo: erro = o recurso não funciona como está; aviso = funciona, mas provavelmente não como se espera */
export type ProblemaPipeline = { nivel: NivelPipeline; recursoId: number | null; texto: string }

export type RecursoFluxo = {
  id: number;
  nome: string;
  tipoRecurso: string;
  ordemExecucao: number;
  recursoPaiId?: number | null;
  codigo?: string | null;
  comandoMapeamentoId?: number | null;
  configuracoes?: Record<string, any> | null;
  temComando?: boolean;
  temComandoMapeamento?: boolean;
  temPublicacaoConfig?: boolean;
  publicacaoConfigId?: number | null;
}

export type EntradaPipeline = {
  recursos: RecursoFluxo[];
  comandos: ComandoPipeline[];
  endpoints: EndpointBasico[];
  // ids dos datasets existentes; null enquanto a lista não foi carregada (não acusa dataset inexistente)
  datasetIds: number[] | null;
  // configurações de publicação por id (para conferir a fonte da exportação); null enquanto não carregadas
  configsPublicacao: { id: number; mapeamentoRecebimentoId?: number | null }[] | null;
}

const PRECISA_COMANDO = ['IMPORTACAO', 'AUTENTICACAO', 'ITERACAO', 'DATASET', 'EXPORTACAO'];
const PRECISA_MAPEAMENTO = ['PROCESSAMENTO', 'VISUALIZACAO'];
const SOBRE_PROCESSAMENTO = ['AGRUPAMENTO', 'FORMATACAO', 'JOIN'];

const rotulo = (r: RecursoFluxo) => `"${r.nome}" (${r.tipoRecurso}, ordem ${r.ordemExecucao})`;

/**
 * Valida o fluxo como o runtime o executa: em ordem crescente, só os recursos sem pai, e cada tipo lendo o que os
 * anteriores produziram. Não chama o backend: usa só o que a tela já carregou.
 */
export function validarPipeline(e: EntradaPipeline): ProblemaPipeline[] {
  const problemas: ProblemaPipeline[] = [];
  const erro = (r: RecursoFluxo, texto: string) => problemas.push({ nivel: 'erro', recursoId: r.id, texto });
  const aviso = (r: RecursoFluxo | null, texto: string) => problemas.push({ nivel: 'aviso', recursoId: r?.id ?? null, texto });

  // empate de ordem desempata pelo id (ordem de criação): a API não garante a ordem entre recursos com a mesma ordem
  const principais = e.recursos.filter(r => !r.recursoPaiId).sort((a, b) => a.ordemExecucao - b.ordemExecucao || a.id - b.id);
  const processamentos = e.recursos.filter(r => r.tipoRecurso === 'PROCESSAMENTO' && !!r.comandoMapeamentoId);

  // Um recurso que trabalha sobre os registros de um Processamento precisa que ele exista e rode antes
  const conferirFonte = (r: RecursoFluxo, mapeamentoId: number | null | undefined, oQue: string) => {
    if (!mapeamentoId) { erro(r, `${rotulo(r)}: ${oQue} não aponta para nenhum mapeamento; sem ele não há registros para processar.`); return; }
    const dono = processamentos.find(p => p.comandoMapeamentoId === mapeamentoId);
    if (!dono) {
      aviso(r, `${rotulo(r)}: ${oQue} aponta para um mapeamento que não pertence a nenhum Processamento deste fluxo.`);
    } else if (dono.ordemExecucao >= r.ordemExecucao) {
      erro(r, `${rotulo(r)}: roda antes do Processamento "${dono.nome}" (ordem ${dono.ordemExecucao}) que produz os registros de que depende.`);
    }
  };

  for (const r of principais) {
    if (PRECISA_COMANDO.includes(r.tipoRecurso) && r.temComando === false) erro(r, `${rotulo(r)}: não tem comando, então nada será executado.`);
    if (PRECISA_MAPEAMENTO.includes(r.tipoRecurso) && r.temComandoMapeamento === false) erro(r, `${rotulo(r)}: não tem mapeamento, então nada será processado.`);
    if (SOBRE_PROCESSAMENTO.includes(r.tipoRecurso)) conferirFonte(r, r.comandoMapeamentoId, 'este recurso');
  }
  // sub-recursos (iteração) também precisam do próprio comando
  for (const r of e.recursos.filter(x => !!x.recursoPaiId)) {
    if (PRECISA_COMANDO.includes(r.tipoRecurso) && r.temComando === false) erro(r, `${rotulo(r)} (executado por item): não tem comando, então nada será executado.`);
  }

  for (const r of principais) {
    const cfg = r.configuracoes ?? {};
    switch (r.tipoRecurso) {
      case 'ITERACAO': {
        const it = cfg['iteracao'];
        const filho = it?.comandoLoop ? e.recursos.find(x => x.id === it.comandoLoop) : undefined;
        if (!filho) {
          // dado migrado: o comandoLoop ficou com o id do recurso de outra base, mas o filho verdadeiro existe neste fluxo
          const filhoReal = e.recursos.find(x => x.recursoPaiId === r.id);
          erro(r, filhoReal
            ? `${rotulo(r)}: aponta para o recurso #${it?.comandoLoop}, que não existe neste fluxo. O recurso por item desta iteração é "${filhoReal.nome}" (#${filhoReal.id}): abra a iteração e salve para corrigir.`
            : `${rotulo(r)}: não tem recurso executado por item (o recurso configurado não existe mais).`);
        }
        else if (filho.recursoPaiId !== r.id) erro(r, `${rotulo(r)}: o recurso por item "${filho.nome}" não está vinculado a esta iteração e também rodaria no fluxo principal.`);
        if (Object.keys(it?.campos ?? {}).length === 0) erro(r, `${rotulo(r)}: não define quais campos de cada item viram variáveis.`);
        break;
      }
      case 'DATASET': {
        const id = cfg['datasetId'];
        if (!id) erro(r, `${rotulo(r)}: não escolheu o dataset que receberá a carga.`);
        else if (e.datasetIds && !e.datasetIds.includes(id)) erro(r, `${rotulo(r)}: usa o dataset #${id}, que não existe.`);
        if ((cfg['camposChave'] ?? []).length === 0) erro(r, `${rotulo(r)}: não define os campos chave da carga.`);
        break;
      }
      case 'JOIN': {
        const j = cfg['join'];
        if (!j?.datasetId) erro(r, `${rotulo(r)}: não escolheu o dataset de referência.`);
        else if (e.datasetIds && !e.datasetIds.includes(j.datasetId)) erro(r, `${rotulo(r)}: usa o dataset #${j.datasetId}, que não existe.`);
        if ((j?.camposChaveDestino ?? []).length === 0) erro(r, `${rotulo(r)}: não define a chave de busca no dataset.`);
        if ((j?.campos ?? []).length === 0) aviso(r, `${rotulo(r)}: não copia nenhum campo do dataset, então não altera os registros.`);
        break;
      }
      case 'AGRUPAMENTO': {
        const a = cfg['agrupamento'];
        if ((a?.chaves ?? []).length === 0) erro(r, `${rotulo(r)}: não define por quais campos agrupar.`);
        if ((a?.tratamentos ?? []).length === 0) erro(r, `${rotulo(r)}: não define nenhum tratamento (o que calcular por grupo).`);
        break;
      }
      case 'FORMATACAO': {
        if ((cfg['formatacoes']?.formatacoes ?? []).length === 0) aviso(r, `${rotulo(r)}: não tem nenhuma regra de formatação, então não altera os registros.`);
        break;
      }
      case 'PUBLICACAO': {
        if (!(r.codigo ?? '').trim()) erro(r, `${rotulo(r)}: não tem código; a URL pública da publicação depende dele.`);
        if (r.temPublicacaoConfig === false) erro(r, `${rotulo(r)}: não tem configuração de publicação.`);
        break;
      }
      case 'EXPORTACAO': {
        if (r.temPublicacaoConfig === false) { erro(r, `${rotulo(r)}: não tem configuração de publicação (fonte e flags de retorno).`); break; }
        const config = e.configsPublicacao?.find(c => c.id === r.publicacaoConfigId);
        if (e.configsPublicacao && config) conferirFonte(r, config.mapeamentoRecebimentoId, 'a fonte da exportação');
        break;
      }
    }
  }

  // Código de publicação repetido entre recursos do fluxo
  const porCodigo = new Map<string, RecursoFluxo[]>();
  principais.filter(r => r.tipoRecurso === 'PUBLICACAO' && (r.codigo ?? '').trim()).forEach(r => {
    const k = (r.codigo as string).trim().toLowerCase();
    porCodigo.set(k, [...(porCodigo.get(k) ?? []), r]);
  });
  porCodigo.forEach((lista, codigo) => {
    if (lista.length > 1) erro(lista[1], `Os recursos ${lista.map(r => `"${r.nome}"`).join(' e ')} usam o mesmo código de publicação "${codigo}".`);
  });

  // Ordem repetida no fluxo principal: a ordem entre eles não é garantida
  const porOrdem = new Map<number, RecursoFluxo[]>();
  principais.forEach(r => porOrdem.set(r.ordemExecucao, [...(porOrdem.get(r.ordemExecucao) ?? []), r]));
  [...porOrdem.entries()].sort((a, b) => a[0] - b[0]).forEach(([ordem, lista]) => {
    if (lista.length > 1) aviso(lista[0], `A ordem ${ordem} é usada por ${lista.map(r => `"${r.nome}"`).join(', ')}: a ordem de execução entre eles não é garantida. Reordene o fluxo.`);
  });

  for (const t of problemasDeTokens(e.recursos.map(r => ({ id: r.id, nome: r.nome, tipoRecurso: r.tipoRecurso, ordemExecucao: r.ordemExecucao, recursoPaiId: r.recursoPaiId })), e.comandos, e.endpoints)) {
    problemas.push({ nivel: 'erro', recursoId: t.recursoId, texto: t.texto });
  }

  // erros primeiro; dentro do nível, na ordem em que o recurso aparece no fluxo
  const posicao = (id: number | null) => principais.find(r => r.id === id || e.recursos.find(x => x.id === id)?.recursoPaiId === r.id)?.ordemExecucao ?? 0;
  return problemas
    .map((p, i) => ({ p, i }))
    .sort((a, b) => (a.p.nivel === b.p.nivel ? 0 : a.p.nivel === 'erro' ? -1 : 1) || posicao(a.p.recursoId) - posicao(b.p.recursoId) || a.i - b.i)
    .map(x => x.p);
}
